import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { studentLabel } from '../utils/studentLabel';
import ScheduleViewerClock, {
  ScheduleNowLine,
  useScheduleNowTick,
} from '../components/ScheduleViewerClock';
import {
  WEEKDAY_FULL,
  WEEKDAY_NAMES,
  TEACHER_TZ,
  addDaysISO,
  formatDateShort,
  formatTimeFromISO,
  minutesFromMidnight,
  mondayOf,
  moscowDateISO,
  nowLineTopPx,
  occurrenceDateMoscow,
  scheduleGridExtent,
  timeFromOccurrenceStart,
  weekDates,
} from '../utils/scheduleDates';

const HOUR_HEIGHT = 48;

function parseHHMM(value, fallback = '11:00') {
  if (!value || !/^\d{1,2}:\d{2}$/.test(value)) return fallback;
  return value.length === 4 ? `0${value}` : value;
}

/** Раскладка пересекающихся отрезков по колонкам внутри дня. */
function assignLanes(items) {
  const sorted = [...items].sort(
    (a, b) => a.startMin - b.startMin || a.endMin - b.endMin || String(a.id).localeCompare(String(b.id))
  );
  const laneEnds = [];
  const withLane = sorted.map((item) => {
    let lane = 0;
    while (lane < laneEnds.length && laneEnds[lane] > item.startMin) lane += 1;
    if (lane === laneEnds.length) laneEnds.push(0);
    laneEnds[lane] = item.endMin;
    return { ...item, lane };
  });
  const laneCount = Math.max(1, ...withLane.map((x) => x.lane + 1));
  return withLane.map((x) => ({ ...x, laneCount }));
}

function laneCellStyle(startTime, durationMinutes, gridStartMin, lane, laneCount) {
  const startMin = minutesFromMidnight((startTime || '00:00').slice(0, 5));
  const top = ((startMin - gridStartMin) / 60) * HOUR_HEIGHT;
  const height = Math.max((durationMinutes / 60) * HOUR_HEIGHT, 28);
  const widthPct = 100 / Math.max(1, laneCount);
  return {
    top: `${top}px`,
    height: `${height}px`,
    left: `calc(${lane * widthPct}% + 2px)`,
    width: `calc(${widthPct}% - 4px)`,
    right: 'auto',
  };
}

/** Список учеников с галочками (вместо Ctrl+клик в multi-select). */
function StudentPicker({ students, selectedIds, onChange, max = 99, label = 'Ученики' }) {
  const selected = selectedIds.map(String);
  const atLimit = selected.length >= max;

  function toggle(id) {
    const sid = String(id);
    if (selected.includes(sid)) {
      onChange(selected.filter((x) => x !== sid));
      return;
    }
    if (max === 1) {
      onChange([sid]);
      return;
    }
    if (atLimit) return;
    onChange([...selected, sid]);
  }

  return (
    <fieldset className="sched-student-picker">
      <legend>
        {label}
        <span className="muted">
          {' '}
          ({selected.length}/{max === 99 ? '∞' : max})
        </span>
      </legend>
      <div className="sched-student-picker-list">
        {students.length === 0 ? (
          <p className="muted">Нет активных учеников</p>
        ) : (
          students.map((s) => {
            const sid = String(s.id);
            const checked = selected.includes(sid);
            const disabled = !checked && atLimit && max > 1;
            return (
              <label key={s.id} className={`sched-student-picker-item${disabled ? ' is-disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(s.id)}
                />
                <span>{studentLabel(s)}</span>
              </label>
            );
          })
        )}
      </div>
    </fieldset>
  );
}

export default function TeacherSchedulePage() {
  const [weekStart, setWeekStart] = useState(() => mondayOf(moscowDateISO()));
  const [settings, setSettings] = useState(null);
  const [occurrences, setOccurrences] = useState([]);
  const [slots, setSlots] = useState([]);
  const [students, setStudents] = useState([]);
  const [highlightStudentId, setHighlightStudentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [slotFormOpen, setSlotFormOpen] = useState(false);
  const [editingSlotId, setEditingSlotId] = useState(null);
  const [slotForm, setSlotForm] = useState({
    weekday: 1,
    start_time: '16:00',
    duration_minutes: 60,
    slot_type: 'individual',
    max_students: 99,
    note: '',
    student_ids: [],
  });

  const [selectedOcc, setSelectedOcc] = useState(null);
  const [rescheduleAt, setRescheduleAt] = useState('');
  /** Минуты только этого переноса; пустое поле не уходит на сервер. */
  const [rescheduleDuration, setRescheduleDuration] = useState('');

  const [oneoffOpen, setOneoffOpen] = useState(false);
  const [oneoffForm, setOneoffForm] = useState({
    start_local: '',
    duration_minutes: 60,
    slot_type: 'individual',
    student_ids: [],
    note: '',
  });

  const activeStudents = useMemo(
    () => students.filter((s) => !s.relation_status || s.relation_status === 'active'),
    [students]
  );

  const studentsById = useMemo(() => {
    const map = new Map();
    for (const s of students) map.set(String(s.id), s);
    return map;
  }, [students]);

  function displayNameFor(id, fallback = '') {
    const s = studentsById.get(String(id));
    if (s) return studentLabel(s) || fallback;
    return fallback;
  }

  function occActiveNames(occ) {
    return (occ.students || [])
      .filter((s) => !s.cancelled_for_student)
      .map((s) => displayNameFor(s.id, s.name))
      .filter(Boolean);
  }

  function occTitle(occ) {
    const names = occActiveNames(occ);
    const name = names[0] || occ.label || 'занятие';
    const start = formatTimeFromISO(occ.start);
    const type = occ.is_group ? 'группа' : 'инд.';
    const parts = [`${name}`, `${start}`, `${occ.duration_minutes || 60} мин`, type];
    if (occ.kind === 'rescheduled' && occ.original_date) {
      parts.push(`вместо ${occ.original_date}`);
    }
    if (occ.kind === 'oneoff') parts.push('разовое');
    if (occ.on_holiday) parts.push(occ.holiday_name || 'праздник');
    return parts.join(' · ');
  }

  function slotTitle(slot) {
    const names = (slot.students || []).map((s) => displayNameFor(s.id, s.name));
    const name = names[0] || slot.note || 'занятие';
    const start = (slot.start_time || '').slice(0, 5);
    const type = slot.slot_type === 'group' ? 'группа' : 'инд.';
    return `${name} · ${start} · ${slot.duration_minutes || 60} мин · ${type}`;
  }
  const [viewMode, setViewMode] = useState('template'); // template | calendar
  const [freeText, setFreeText] = useState('');
  const [holidays, setHolidays] = useState([]);
  const [holidayInput, setHolidayInput] = useState('');
  const [holidayName, setHolidayName] = useState('');
  const [holidayHits, setHolidayHits] = useState([]);
  const [examStudentId, setExamStudentId] = useState('');
  const [examDate, setExamDate] = useState('');
  const [examCount, setExamCount] = useState(null);
  const [changeRequests, setChangeRequests] = useState([]);
  const [resolveBusyId, setResolveBusyId] = useState(null);
  const now = useScheduleNowTick();
  const today = moscowDateISO(now);

  const workStart = parseHHMM(settings?.work_start, '11:00');
  const workEnd = parseHHMM(settings?.work_end, '21:00');
  const gridSegments = useMemo(() => {
    const segs = [];
    for (const slot of slots) {
      segs.push({ start: slot.start_time, duration: slot.duration_minutes });
      for (const s of slot.students || []) {
        if (!s.start_time) continue;
        segs.push({
          start: s.start_time,
          duration: s.duration_minutes || slot.duration_minutes || 60,
        });
      }
    }
    for (const occ of occurrences) {
      segs.push({
        start: timeFromOccurrenceStart(occ.start),
        duration: occ.duration_minutes,
      });
      for (const s of occ.students || []) {
        if (!s.start_time) continue;
        segs.push({
          start: s.start_time,
          duration: s.duration_minutes || occ.duration_minutes || 60,
        });
      }
    }
    return segs;
  }, [slots, occurrences]);
  const { gridStartMin, gridHours } = useMemo(
    () => scheduleGridExtent(workStart, workEnd, gridSegments),
    [workStart, workEnd, gridSegments]
  );
  const days = useMemo(() => weekDates(weekStart), [weekStart]);

  const loadStudents = useCallback(async () => {
    try {
      const res = await api.get('/teacher/students/');
      const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setStudents(list);
    } catch {
      setStudents([]);
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const weekEnd = addDaysISO(weekStart, 6);
      const [calRes, slotsRes, settingsRes, freeRes, holRes, reqRes] = await Promise.all([
        api.get('/scheduler/teacher/calendar/', {
          params: { from: weekStart, to: weekEnd },
        }),
        api.get('/scheduler/teacher/slots/'),
        api.get('/scheduler/teacher/settings/'),
        api.get('/scheduler/teacher/free-text/'),
        api.get('/scheduler/teacher/holidays/'),
        api.get('/scheduler/teacher/change-requests/', { params: { status: 'pending' } }).catch(() => ({ data: [] })),
      ]);
      setOccurrences(calRes.data.occurrences || []);
      setSlots(slotsRes.data || []);
      setSettings(settingsRes.data);
      setFreeText(freeRes.data?.text || '');
      setHolidays(holRes.data || []);
      setChangeRequests(Array.isArray(reqRes.data) ? reqRes.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось загрузить расписание');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  async function resolveChangeRequest(id, action) {
    setResolveBusyId(id);
    setMessage('');
    try {
      await api.post(`/scheduler/teacher/change-requests/${id}/resolve/`, { action });
      setMessage(action === 'approve' ? 'Запрос одобрен' : 'Запрос отклонён');
      await reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось обработать запрос');
    } finally {
      setResolveBusyId(null);
    }
  }

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function saveSettings(patch) {
    setMessage('');
    try {
      const res = await api.patch('/scheduler/teacher/settings/', patch);
      setSettings(res.data);
      setMessage('Настройки сохранены');
      reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка сохранения настроек');
    }
  }

  async function saveSlot(e) {
    e.preventDefault();
    setError('');
    const payload = {
      weekday: Number(slotForm.weekday),
      start_time: slotForm.start_time,
      duration_minutes: Number(slotForm.duration_minutes),
      slot_type: slotForm.slot_type,
      max_students: 1,
      note: slotForm.note,
    };
    const nextIds = slotForm.student_ids.map(String).slice(0, 1);
    const wasEdit = Boolean(editingSlotId);
    try {
      let slotId = editingSlotId;
      if (wasEdit) {
        await api.patch(`/scheduler/teacher/slots/${editingSlotId}/`, payload);
      } else {
        const res = await api.post('/scheduler/teacher/slots/', {
          ...payload,
          student_ids: nextIds.map(Number),
        });
        slotId = res.data?.id;
      }
      if (wasEdit && slotId) {
        const prev = new Set(
          (slots.find((s) => s.id === slotId)?.students || []).map((s) => String(s.id))
        );
        const nextSet = new Set(nextIds);
        for (const id of nextIds) {
          if (!prev.has(id)) {
            await api.post(`/scheduler/teacher/slots/${slotId}/enrollments/`, {
              student_id: Number(id),
            });
          }
        }
        for (const id of prev) {
          if (!nextSet.has(id)) {
            await api.delete(`/scheduler/teacher/slots/${slotId}/enrollments/`, {
              data: { student_id: Number(id) },
            });
          }
        }
      }
      setSlotFormOpen(false);
      setEditingSlotId(null);
      setMessage(wasEdit ? 'Занятие обновлено' : 'Занятие добавлено');
      reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось сохранить занятие');
    }
  }

  function openCreateSlot() {
    setEditingSlotId(null);
    setSlotForm({
      weekday: 1,
      start_time: '16:00',
      duration_minutes: 60,
      slot_type: 'individual',
      max_students: 1,
      note: '',
      student_ids: [],
    });
    setSlotFormOpen(true);
  }

  function openEditSlot(slot) {
    setEditingSlotId(slot.id);
    setSlotForm({
      weekday: slot.weekday,
      start_time: (slot.start_time || '16:00').slice(0, 5),
      duration_minutes: slot.duration_minutes || 60,
      slot_type: slot.slot_type || 'individual',
      max_students: 1,
      note: slot.note || '',
      student_ids: (slot.students || []).map((s) => String(s.id)).slice(0, 1),
    });
    setSlotFormOpen(true);
  }

  async function deleteSlot(id) {
    if (!window.confirm('Удалить это занятие из постоянного расписания?')) return;
    await api.delete(`/scheduler/teacher/slots/${id}/`);
    reload();
  }

  function durationForReschedule(occ) {
    return String(occ.duration_minutes || 60);
  }

  function openOccModal(occ) {
    setSelectedOcc(occ);
    setRescheduleDuration(durationForReschedule(occ));
    setRescheduleAt(
      occ.kind === 'rescheduled'
        ? `${occurrenceDateMoscow(occ.start)}T${timeFromOccurrenceStart(occ.start)}`
        : ''
    );
  }

  function closeOccModal() {
    setSelectedOcc(null);
    setRescheduleAt('');
    setRescheduleDuration('');
  }

  async function cancelOccurrence(occ) {
    if (!occ.weekly_slot_id || !occ.original_date) {
      if (occ.oneoff_id) {
        await api.patch(`/scheduler/teacher/oneoffs/${occ.oneoff_id}/`, { is_cancelled: true });
        closeOccModal();
        reload();
        return;
      }
      setError('Нельзя отменить это вхождение');
      return;
    }
    try {
      await api.post('/scheduler/teacher/exceptions/', {
        weekly_slot_id: occ.weekly_slot_id,
        original_date: occ.original_date,
        kind: 'cancel',
        student_id: null,
      });
      closeOccModal();
      setMessage('Занятие отменено');
      reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка отмены');
    }
  }

  async function rescheduleOccurrence(occ) {
    if (!occ.weekly_slot_id || !occ.original_date || !rescheduleAt) {
      setError('Укажите новое время');
      return;
    }
    const minutes = Number(rescheduleDuration);
    if (!Number.isFinite(minutes) || minutes < 15 || minutes > 480) {
      setError('Длительность переноса: от 15 до 480 минут');
      return;
    }
    const updating = occ.kind === 'rescheduled' && occ.exception_id;
    try {
      await api.post('/scheduler/teacher/exceptions/', {
        weekly_slot_id: occ.weekly_slot_id,
        original_date: occ.original_date,
        kind: 'reschedule',
        student_id: null,
        exception_id: updating ? occ.exception_id : null,
        new_start: rescheduleAt.length === 16 ? `${rescheduleAt}:00` : rescheduleAt,
        new_duration_minutes: minutes,
      });
      closeOccModal();
      setMessage(updating ? 'Перенос обновлён' : 'Занятие перенесено');
      reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка переноса');
    }
  }

  async function createOneoff(e) {
    e.preventDefault();
    try {
      const local = oneoffForm.start_local;
      const iso = local.length === 16 ? `${local}:00` : local;
      await api.post('/scheduler/teacher/oneoffs/', {
        start_time: iso,
        duration_minutes: Number(oneoffForm.duration_minutes),
        slot_type: oneoffForm.slot_type,
        student_ids: oneoffForm.student_ids.map(Number),
        note: oneoffForm.note,
      });
      setOneoffOpen(false);
      setMessage('Разовое занятие создано');
      reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось создать разовое');
    }
  }

  async function addHolidays(e) {
    e.preventDefault();
    const dates = holidayInput
      .split(/[\s,;]+/)
      .map((x) => x.trim())
      .filter(Boolean);
    if (!dates.length) return;
    try {
      const res = await api.post('/scheduler/teacher/holidays/', {
        dates,
        name: holidayName,
      });
      setHolidayHits(res.data.lesson_hits || []);
      setHolidayInput('');
      setMessage('Праздники добавлены');
      reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка праздников');
    }
  }

  async function removeHoliday(id) {
    await api.delete(`/scheduler/teacher/holidays/${id}/`);
    reload();
  }

  async function saveExam(e) {
    e.preventDefault();
    if (!examStudentId || !examDate) return;
    try {
      const res = await api.put('/scheduler/teacher/exam-dates/', {
        student_id: Number(examStudentId),
        exam_date: examDate,
        label: 'Экзамен',
      });
      setExamCount(res.data.count);
      setMessage('Дата экзамена сохранена');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка даты экзамена');
    }
  }

  async function loadExamCount(studentId) {
    if (!studentId) {
      setExamCount(null);
      return;
    }
    try {
      const res = await api.get(`/scheduler/teacher/exam-count/${studentId}/`);
      setExamCount(res.data);
      if (res.data.exam_date) setExamDate(res.data.exam_date);
    } catch {
      setExamCount(null);
    }
  }

  function slotClass(slot) {
    const parts = ['sched-occ'];
    if (slot.slot_type === 'group' || slot.is_group) parts.push('sched-occ--group');
    if (highlightStudentId) {
      const hit = (slot.students || []).some((s) => String(s.id) === String(highlightStudentId));
      parts.push(hit ? 'sched-occ--highlight' : 'sched-occ--dim');
    }
    return parts.join(' ');
  }

  const slotsByWeekday = useMemo(() => {
    const map = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    for (const slot of slots) {
      if (slot.is_active === false) continue;
      map[slot.weekday]?.push(slot);
    }
    for (const wd of Object.keys(map)) {
      const items = (map[wd] || []).map((slot) => {
        const startLabel = (slot.start_time || '00:00').slice(0, 5);
        const dur = slot.duration_minutes || 60;
        const startMin = minutesFromMidnight(startLabel);
        return {
          ...slot,
          startLabel,
          dur,
          startMin,
          endMin: startMin + dur,
          id: slot.id,
        };
      });
      map[wd] = assignLanes(items);
    }
    return map;
  }, [slots]);

  function occClass(occ) {
    const parts = ['sched-occ'];
    if (occ.kind === 'rescheduled') parts.push('sched-occ--moved');
    if (occ.kind === 'oneoff') parts.push('sched-occ--oneoff');
    if (occ.is_group) parts.push('sched-occ--group');
    if (occ.on_holiday) parts.push('sched-occ--holiday');
    if (highlightStudentId) {
      const hit = (occ.students || []).some(
        (s) => String(s.id) === String(highlightStudentId) && !s.cancelled_for_student
      );
      parts.push(hit ? 'sched-occ--highlight' : 'sched-occ--dim');
    }
    return parts.join(' ');
  }

  const occByDay = useMemo(() => {
    const map = Object.fromEntries(days.map((d) => [d, []]));
    for (const occ of occurrences) {
      if (occ.is_cancelled) continue;
      const d = occurrenceDateMoscow(occ.start);
      if (!map[d]) continue;
      const startLabel = timeFromOccurrenceStart(occ.start);
      const dur = occ.duration_minutes || 60;
      const startMin = minutesFromMidnight(startLabel);
      map[d].push({
        ...occ,
        startLabel,
        dur,
        startMin,
        endMin: startMin + dur,
        id: occ.key,
      });
    }
    for (const d of days) {
      map[d] = assignLanes(map[d] || []);
    }
    return map;
  }, [occurrences, days]);

  const hourLabels = Array.from({ length: gridHours + 1 }, (_, i) => {
    const mins = gridStartMin + i * 60;
    const h = Math.floor(mins / 60);
    return `${String(h).padStart(2, '0')}:00`;
  });

  const nowLineTop = useMemo(
    () =>
      viewMode === 'calendar'
        ? nowLineTopPx({
            now,
            workStart,
            workEnd,
            hourHeight: HOUR_HEIGHT,
            todayIso: today,
          })
        : null,
    [viewMode, now, workStart, workEnd, today],
  );

  return (
    <div className="sched-page">
      <div className="page-header">
        <h1>Расписание</h1>
        <p className="page-subtitle">
          <strong>Расписание</strong> — постоянный шаблон («каждую среду в 19:00»).{' '}
          <strong>Календарь</strong> — конкретные даты: отмены, переносы и разовые.
          Журнал прошедших — в <Link to="/teacher/lessons">уроках</Link>
          {' · '}
          <Link to="/teacher/messages">сообщения</Link>.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {changeRequests.length > 0 && (
        <section className="sched-panel ops-queue-panel">
          <h2>Очередь: переносы и отмены ({changeRequests.length})</h2>
          <p className="muted">Запросы учеников и родителей. Одобрение применяет отмену/перенос в календаре.</p>
          <ul className="sched-upcoming-list">
            {changeRequests.map((r) => (
              <li key={r.id}>
                <div className="sched-upcoming-date">
                  {r.student_name}
                  {' · '}
                  {r.kind === 'cancel' ? 'отмена' : 'перенос'}
                  {r.occurrence_start
                    ? ` · ${formatDateShort(occurrenceDateMoscow(r.occurrence_start))} ${formatTimeFromISO(r.occurrence_start)}`
                    : ` · ${r.occurrence_key}`}
                </div>
                <div className="sched-upcoming-meta">
                  {r.note || 'без комментария'}
                  {r.proposed_new_start
                    ? ` → ${formatDateShort(occurrenceDateMoscow(r.proposed_new_start))} ${formatTimeFromISO(r.proposed_new_start)}`
                    : ''}
                </div>
                <div className="ops-change-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={resolveBusyId === r.id}
                    onClick={() => resolveChangeRequest(r.id, 'approve')}
                  >
                    Одобрить
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    disabled={resolveBusyId === r.id}
                    onClick={() => resolveChangeRequest(r.id, 'reject')}
                  >
                    Отклонить
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="sched-mode-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={`sched-mode-tab${viewMode === 'template' ? ' is-active' : ''}`}
          aria-selected={viewMode === 'template'}
          onClick={() => setViewMode('template')}
        >
          Расписание (шаблон)
        </button>
        <button
          type="button"
          role="tab"
          className={`sched-mode-tab${viewMode === 'calendar' ? ' is-active' : ''}`}
          aria-selected={viewMode === 'calendar'}
          onClick={() => setViewMode('calendar')}
        >
          Календарь (неделя)
        </button>
      </div>

      <div className="sched-toolbar">
        {viewMode === 'calendar' ? (
          <div className="sched-week-nav">
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setWeekStart(addDaysISO(weekStart, -7))}>
              ← Неделя
            </button>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setWeekStart(mondayOf(moscowDateISO()))}
            >
              Сегодня
            </button>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setWeekStart(addDaysISO(weekStart, 7))}>
              Неделя →
            </button>
            <span className="sched-week-label">
              {formatDateShort(weekStart)} — {formatDateShort(addDaysISO(weekStart, 6))}
            </span>
          </div>
        ) : (
          <p className="sched-toolbar-note muted">
            Постоянные занятия. Отмена на одну среду не убирает её из шаблона — здесь по-прежнему видно как занятую.
          </p>
        )}
        <div className="sched-toolbar-actions">
          <ScheduleViewerClock now={now} viewerTz={TEACHER_TZ} />
          <label className="sched-filter">
            Подсветить ученика
            <select
              value={highlightStudentId}
              onChange={(e) => setHighlightStudentId(e.target.value)}
            >
              <option value="">Все</option>
              {activeStudents.map((s) => (
                <option key={s.id} value={s.id}>
                  {studentLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="btn btn-primary btn-sm" onClick={openCreateSlot}>
            + Занятие
          </button>
          {viewMode === 'calendar' && (
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setOneoffOpen(true)}>
              + Разовое
            </button>
          )}
        </div>
      </div>

      {viewMode === 'calendar' && (
        <p className="sched-legend muted">
          Синий — индивидуальное, зелёный — группа.
          Янтарная полоска — перенос на эту дату, фиолетовая — разовое.
          Отменённые в календарь не попадают. Постоянно поменять время — в шаблоне.
        </p>
      )}

      {loading ? (
        <p>Загрузка…</p>
      ) : viewMode === 'template' ? (
        <div className="sched-grid-wrap">
          <div className="sched-grid" style={{ '--sched-hour-h': `${HOUR_HEIGHT}px` }}>
            <div className="sched-grid-corner" />
            {WEEKDAY_NAMES.map((name) => (
              <div key={name} className="sched-day-head">
                <div className="sched-day-name">{name}</div>
                <div className="sched-day-date">каждую неделю</div>
              </div>
            ))}
            <div className="sched-hours">
              {hourLabels.map((label) => (
                <div key={label} className="sched-hour-label" style={{ height: HOUR_HEIGHT }}>
                  {label}
                </div>
              ))}
            </div>
            {[0, 1, 2, 3, 4, 5, 6].map((wd) => (
              <div
                key={wd}
                className="sched-day-col"
                style={{ height: gridHours * HOUR_HEIGHT }}
              >
                {hourLabels.slice(0, -1).map((label) => (
                  <div key={label} className="sched-hour-line" style={{ height: HOUR_HEIGHT }} />
                ))}
                {(slotsByWeekday[wd] || []).map((slot) => {
                  const names = (slot.students || []).map((s) => displayNameFor(s.id, s.name));
                  const name = names[0] || slot.note || 'занятие';
                  const start = slot.startLabel || (slot.start_time || '').slice(0, 5);
                  return (
                    <button
                      key={slot.id}
                      type="button"
                      className={slotClass(slot)}
                      style={laneCellStyle(
                        start,
                        slot.dur || slot.duration_minutes || 60,
                        gridStartMin,
                        slot.lane || 0,
                        slot.laneCount || 1
                      )}
                      onClick={() => openEditSlot(slot)}
                      title={slotTitle(slot)}
                    >
                      <span className="sched-occ-meta">
                        <span className="sched-occ-time">{start}</span>
                      </span>
                      <span className="sched-occ-people">
                        <span
                          className={`sched-occ-person${names.length ? '' : ' sched-occ-person--empty'}`}
                        >
                          {name}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="sched-grid-wrap">
          <div className="sched-grid" style={{ '--sched-hour-h': `${HOUR_HEIGHT}px` }}>
            <div className="sched-grid-corner" />
            {days.map((d, i) => (
              <div key={d} className={`sched-day-head${d === today ? ' sched-day-head--today' : ''}`}>
                <div className="sched-day-name">{WEEKDAY_NAMES[i]}</div>
                <div className="sched-day-date">{formatDateShort(d)}</div>
              </div>
            ))}
            <div className="sched-hours">
              {hourLabels.map((label) => (
                <div key={label} className="sched-hour-label" style={{ height: HOUR_HEIGHT }}>
                  {label}
                </div>
              ))}
            </div>
            {days.map((d) => (
              <div
                key={d}
                className={`sched-day-col${d === today ? ' sched-day-col--today' : ''}`}
                style={{ height: gridHours * HOUR_HEIGHT }}
              >
                {hourLabels.slice(0, -1).map((label) => (
                  <div key={label} className="sched-hour-line" style={{ height: HOUR_HEIGHT }} />
                ))}
                {d === today && <ScheduleNowLine topPx={nowLineTop} />}
                {(occByDay[d] || []).map((occ) => {
                  const names = occActiveNames(occ);
                  const name = names[0] || occ.label || 'занятие';
                  const start = occ.startLabel || formatTimeFromISO(occ.start);
                  return (
                    <button
                      key={occ.key}
                      type="button"
                      className={occClass(occ)}
                      style={laneCellStyle(
                        start,
                        occ.dur || occ.duration_minutes || 60,
                        gridStartMin,
                        occ.lane || 0,
                        occ.laneCount || 1
                      )}
                      onClick={() => openOccModal(occ)}
                      title={occTitle(occ)}
                    >
                      <span className="sched-occ-meta">
                        <span className="sched-occ-time">{start}</span>
                      </span>
                      <span className="sched-occ-people">
                        <span
                          className={`sched-occ-person${names.length ? '' : ' sched-occ-person--empty'}`}
                        >
                          {name}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      <section className="sched-panel">
        <h2>Постоянное расписание</h2>
        <p className="muted sched-panel-hint">
          Каждая карточка — одно занятие с одним учеником. Зелёный — группа, синий — индивидуально.
          Клик в шаблоне — правка каждую неделю. В календаре — отмена или перенос только на эту дату.
        </p>
        {slots.length === 0 ? (
          <p className="muted">Пока нет занятий. Добавьте «каждый вторник в 16:00».</p>
        ) : (
          <ul className="sched-slot-list">
            {slots.map((slot) => (
              <li key={slot.id}>
                <div className="sched-slot-list-main">
                  <strong>{WEEKDAY_FULL[slot.weekday]}</strong> {slot.start_time}, {slot.duration_minutes} мин
                  {' — '}
                  {slot.slot_type === 'group' ? 'группа' : 'инд.'}
                  {': '}
                  {(slot.students || [])
                    .map((s) => displayNameFor(s.id, s.name))
                    .join(', ') || 'пусто'}
                  {slot.note ? ` (${slot.note})` : ''}
                </div>
                <div className="sched-slot-list-actions">
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => openEditSlot(slot)}>
                    Изменить
                  </button>
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => deleteSlot(slot.id)}>
                    Удалить
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="sched-panel sched-panel--grid2">
        <div>
          <h2>Рабочие часы и ссылки</h2>
          {settings && (
            <div className="sched-settings-form">
              <label>
                С
                <input
                  type="time"
                  value={settings.work_start?.slice(0, 5)}
                  onChange={(e) => setSettings({ ...settings, work_start: e.target.value })}
                />
              </label>
              <label>
                До
                <input
                  type="time"
                  value={settings.work_end?.slice(0, 5)}
                  onChange={(e) => setSettings({ ...settings, work_end: e.target.value })}
                />
              </label>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() =>
                  saveSettings({
                    work_start: settings.work_start,
                    work_end: settings.work_end,
                    working_weekdays: settings.working_weekdays,
                  })
                }
              >
                Сохранить окно
              </button>
              <div className="sched-link-block">
                <div>
                  Публичная ссылка (без имён):
                  <br />
                  <a href={settings.public_url} target="_blank" rel="noreferrer">
                    {settings.public_url}
                  </a>
                </div>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => saveSettings({ rotate_public_token: true })}
                >
                  Сменить токен
                </button>
              </div>
              <div className="sched-link-block">
                <div>
                  Яндекс / iCal (полное расписание):
                  <br />
                  <code>{settings.ical_url}</code>
                </div>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => navigator.clipboard?.writeText(settings.ical_url)}
                >
                  Копировать ссылку
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => saveSettings({ rotate_ical_token: true })}
                >
                  Сменить iCal
                </button>
                <p className="muted sched-ical-hint">
                  В Яндекс.Календаре слева: <strong>Подписки → Добавить</strong> — вставьте эту
                  ссылку целиком. Не импортируйте файл и не скачивайте .ics: тогда события
                  останутся копией и не обновятся. Яндекс сам подтягивает изменения раз в
                  несколько часов, иногда до двух суток; кнопки «обновить сейчас» нет.
                </p>
              </div>
            </div>
          )}
        </div>
        <div>
          <h2>Свободное время</h2>
          <pre className="sched-free-text">{freeText || '—'}</pre>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => navigator.clipboard?.writeText(freeText)}>
            Копировать
          </button>
        </div>
      </section>

      <section className="sched-panel sched-panel--grid2">
        <div>
          <h2>Праздники</h2>
          <form className="sched-inline-form" onSubmit={addHolidays}>
            <input
              type="text"
              placeholder="2026-01-01, 2026-01-07"
              value={holidayInput}
              onChange={(e) => setHolidayInput(e.target.value)}
            />
            <input
              type="text"
              placeholder="Название"
              value={holidayName}
              onChange={(e) => setHolidayName(e.target.value)}
            />
            <button type="submit" className="btn btn-primary btn-sm">
              Добавить
            </button>
          </form>
          <ul className="sched-slot-list">
            {holidays.map((h) => (
              <li key={h.id}>
                {h.date} {h.name}
                <button type="button" className="btn btn-outline btn-sm" onClick={() => removeHoliday(h.id)}>
                  Удалить
                </button>
              </li>
            ))}
          </ul>
          {holidayHits.length > 0 && (
            <div className="sched-hits">
              <strong>Занятия в эти дни:</strong>
              <ul>
                {holidayHits.map((hit) => (
                  <li key={hit.key}>
                    {formatTimeFromISO(hit.start)} — {hit.label}
                    {hit.is_cancelled ? ' (уже отменено)' : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div>
          <h2>Занятия до экзамена</h2>
          <form className="sched-inline-form" onSubmit={saveExam}>
            <select
              value={examStudentId}
              onChange={(e) => {
                setExamStudentId(e.target.value);
                loadExamCount(e.target.value);
              }}
            >
              <option value="">Ученик…</option>
                  {students
                    .filter((s) => !s.relation_status || s.relation_status === 'active')
                    .map((s) => (
                    <option key={s.id} value={s.id}>
                      {studentLabel(s)}
                    </option>
                  ))}
            </select>
            <input type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)} />
            <button type="submit" className="btn btn-primary btn-sm">
              Сохранить
            </button>
          </form>
          {examCount && (
            <div className="sched-exam-count">
              {examCount.warning ? (
                <p>{examCount.warning}</p>
              ) : (
                <>
                  <p>
                    До {examCount.exam_date}: <strong>{examCount.count}</strong> занятий
                    (праздники не считаются).
                  </p>
                  {examCount.holiday_hits?.length > 0 && (
                    <p className="muted">
                      На праздники попадает: {examCount.holiday_hits.length} — перенесите или отмените.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {slotFormOpen && (
        <div
          className="sched-modal-backdrop"
          role="presentation"
          onClick={() => {
            setSlotFormOpen(false);
            setEditingSlotId(null);
          }}
        >
          <form
            className="sched-modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={saveSlot}
          >
            <h3>{editingSlotId ? 'Изменить занятие' : 'Постоянное занятие'}</h3>
            <label>
              День
              <select
                value={slotForm.weekday}
                onChange={(e) => setSlotForm({ ...slotForm, weekday: Number(e.target.value) })}
              >
                {WEEKDAY_FULL.map((name, i) => (
                  <option key={name} value={i}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Время
              <input
                type="time"
                value={slotForm.start_time}
                onChange={(e) => setSlotForm({ ...slotForm, start_time: e.target.value })}
                required
              />
            </label>
            <label>
              Минут
              <input
                type="number"
                min={15}
                max={480}
                value={slotForm.duration_minutes}
                onChange={(e) => setSlotForm({ ...slotForm, duration_minutes: e.target.value })}
              />
            </label>
            <label>
              Тип
              <select
                value={slotForm.slot_type}
                onChange={(e) =>
                  setSlotForm({
                    ...slotForm,
                    slot_type: e.target.value,
                    student_ids: slotForm.student_ids.slice(0, 1),
                  })
                }
              >
                <option value="individual">Индивидуальное</option>
                <option value="group">Группа</option>
              </select>
            </label>
            <StudentPicker
              students={activeStudents}
              selectedIds={slotForm.student_ids}
              max={1}
              onChange={(ids) => setSlotForm({ ...slotForm, student_ids: ids.slice(0, 1) })}
            />
            <label>
              Пометка
              <input
                value={slotForm.note}
                onChange={(e) => setSlotForm({ ...slotForm, note: e.target.value })}
                placeholder="Например: ОГЭ / ЕГЭ"
              />
            </label>
            <div className="sched-modal-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setSlotFormOpen(false);
                  setEditingSlotId(null);
                }}
              >
                Отмена
              </button>
              <button type="submit" className="btn btn-primary">
                {editingSlotId ? 'Сохранить' : 'Создать'}
              </button>
            </div>
          </form>
        </div>
      )}

      {oneoffOpen && (
        <div className="sched-modal-backdrop" role="presentation" onClick={() => setOneoffOpen(false)}>
          <form className="sched-modal" onClick={(e) => e.stopPropagation()} onSubmit={createOneoff}>
            <h3>Разовое занятие</h3>
            <label>
              Дата и время
              <input
                type="datetime-local"
                value={oneoffForm.start_local}
                onChange={(e) => setOneoffForm({ ...oneoffForm, start_local: e.target.value })}
                required
              />
            </label>
            <label>
              Минут
              <input
                type="number"
                value={oneoffForm.duration_minutes}
                onChange={(e) => setOneoffForm({ ...oneoffForm, duration_minutes: e.target.value })}
              />
            </label>
            <label>
              Тип
              <select
                value={oneoffForm.slot_type}
                onChange={(e) =>
                  setOneoffForm({
                    ...oneoffForm,
                    slot_type: e.target.value,
                    student_ids: oneoffForm.student_ids.slice(0, 1),
                  })
                }
              >
                <option value="individual">Индивидуальное</option>
                <option value="group">Группа</option>
              </select>
            </label>
            <StudentPicker
              students={activeStudents}
              selectedIds={oneoffForm.student_ids}
              max={1}
              onChange={(ids) => setOneoffForm({ ...oneoffForm, student_ids: ids.slice(0, 1) })}
            />
            <div className="sched-modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setOneoffOpen(false)}>
                Отмена
              </button>
              <button type="submit" className="btn btn-primary">
                Создать
              </button>
            </div>
          </form>
        </div>
      )}

      {selectedOcc && (
          <div
            className="sched-modal-backdrop"
            role="presentation"
            onClick={closeOccModal}
          >
            <div className="sched-modal" onClick={(e) => e.stopPropagation()} role="dialog">
              <h3>{occTitle(selectedOcc)}</h3>
              <p>
                {formatTimeFromISO(selectedOcc.start)} · {selectedOcc.duration_minutes} мин
                {selectedOcc.is_group ? ' · группа' : ' · инд.'}
                {selectedOcc.kind === 'rescheduled' && selectedOcc.original_date
                  ? ` · вместо ${selectedOcc.original_date}`
                  : ''}
                {selectedOcc.kind === 'oneoff' ? ' · разовое' : ''}
                {selectedOcc.on_holiday ? ` · ${selectedOcc.holiday_name || 'праздник'}` : ''}
              </p>
              {selectedOcc.weekly_slot_id && (
                <div className="sched-modal-actions">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      const slot = slots.find((s) => s.id === selectedOcc.weekly_slot_id);
                      if (!slot) {
                        setError('Занятие шаблона не найдено — обновите страницу');
                        return;
                      }
                      closeOccModal();
                      openEditSlot(slot);
                    }}
                  >
                    Изменить каждую неделю…
                  </button>
                </div>
              )}
              <p className="muted">
                Отмена и перенос действуют <strong>только на эту дату</strong>. Длительность можно
                задать отдельно для переноса: шаблон («каждую неделю») не меняется.
              </p>
              <div className="sched-modal-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => cancelOccurrence(selectedOcc)}
                >
                  Отменить только эту дату
                </button>
              </div>
              {selectedOcc.weekly_slot_id && (
                <div className="sched-reschedule-row">
                  <label>
                    {selectedOcc.kind === 'rescheduled' ? 'Время переноса' : 'Перенести на'}
                    <input
                      type="datetime-local"
                      value={rescheduleAt}
                      onChange={(e) => setRescheduleAt(e.target.value)}
                    />
                  </label>
                  <label>
                    Минут на этот перенос
                    <input
                      type="number"
                      min={15}
                      max={480}
                      value={rescheduleDuration}
                      onChange={(e) => setRescheduleDuration(e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => rescheduleOccurrence(selectedOcc)}
                  >
                    {selectedOcc.kind === 'rescheduled' ? 'Сохранить перенос' : 'Перенести только эту дату'}
                  </button>
                </div>
              )}
              <div className="sched-modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeOccModal}>
                  Закрыть
                </button>
              </div>
            </div>
          </div>
      )}
    </div>
  );
}
